import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import AppLayout from "../AppLayout";
import { API } from "../apiConfig";
import "../css/Firm.css";
import { Plus, Search, Edit, Trash2, Save, X } from "lucide-react";

const emptyForm = {
  firm_name: "",
  gst_no: "",
  mobile: "",
  email: "",
  address: "",
  bank_name: "",
  account_no: "",
  ifsc_code: "",
  terms_conditions: "",
  logo: null,
  logo_url: "",
};

function Firm() {
  const [firms, setFirms] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const authHeader = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
    },
  });

  useEffect(() => {
    loadFirms();
  }, []);

  const loadFirms = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/firms`, {
        ...authHeader(),
        params: { search, per_page: 50 },
      });
      setFirms(res.data.data.data || []);
    } catch {
      Swal.fire("Error", "Firm loading failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogo = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setForm((prev) => ({
      ...prev,
      logo: file,
      logo_url: URL.createObjectURL(file),
    }));
  };

  const saveFirm = async (e) => {
    e.preventDefault();

    if (!form.firm_name.trim()) {
      Swal.fire("Required", "Please enter firm name", "warning");
      return;
    }

    try {
      const fd = new FormData();

      fd.append("firm_name", form.firm_name);
      fd.append("gst_no", form.gst_no || "");
      fd.append("mobile", form.mobile || "");
      fd.append("email", form.email || "");
      fd.append("address", form.address || "");
      fd.append("bank_name", form.bank_name || "");
      fd.append("account_no", form.account_no || "");
      fd.append("ifsc_code", form.ifsc_code || "");
      fd.append("terms_conditions", form.terms_conditions || "");

      if (form.logo) {
        fd.append("logo", form.logo);
      }

      if (editId) {
        await axios.post(`${API}/firms/${editId}`, fd, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
            "Content-Type": "multipart/form-data",
          },
        });

        Swal.fire("Updated", "Firm updated successfully", "success");
      } else {
        await axios.post(`${API}/firms`, fd, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
            "Content-Type": "multipart/form-data",
          },
        });

        Swal.fire("Saved", "Firm saved successfully", "success");
      }

      closeForm();
      loadFirms();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Firm save failed";

      Swal.fire("Error", msg, "error");
    }
  };

  const editFirm = async (id) => {
    try {
      const res = await axios.get(`${API}/firms/${id}`, authHeader());
      const data = res.data.data;

      setForm({
        firm_name: data.firm_name || "",
        gst_no: data.gst_no || "",
        mobile: data.mobile || "",
        email: data.email || "",
        address: data.address || "",
        bank_name: data.bank_name || "",
        account_no: data.account_no || "",
        ifsc_code: data.ifsc_code || "",
        terms_conditions: data.terms_conditions || "",
        logo: null,
        logo_url: data.logo_url || "",
      });

      setEditId(id);
      setShowForm(true);
    } catch {
      Swal.fire("Error", "Firm loading failed", "error");
    }
  };

  const deleteFirm = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Firm?",
      text: "This firm will be deleted permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API}/firms/${id}`, authHeader());
      Swal.fire("Deleted", "Firm deleted successfully", "success");
      loadFirms();
    } catch {
      Swal.fire("Error", "Firm delete failed", "error");
    }
  };

  return (
    <AppLayout
      title={showForm ? (editId ? "Edit Firm" : "New Firm") : "Firm Registration"}
      subtitle="Manage firm profile for billing and print"
    >
      {showForm ? (
        <div className="firm-page">
          <div className="firm-header-card">
            <div>
              <h3>{editId ? "Update Firm" : "Create Firm"}</h3>
              <p>Firm details will be used on bill print format</p>
            </div>

            <button className="firm-close-btn" onClick={closeForm}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={saveFirm} className="firm-form-card">
            <div className="firm-grid">
              <div className="firm-field">
                <label>Firm Name *</label>
                <input
                  name="firm_name"
                  value={form.firm_name}
                  onChange={handleChange}
                  placeholder="Enter firm name"
                />
              </div>

              <div className="firm-field">
                <label>GST No</label>
                <input
                  name="gst_no"
                  value={form.gst_no}
                  onChange={handleChange}
                  placeholder="GST number"
                />
              </div>

              <div className="firm-field">
                <label>Mobile</label>
                <input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="Mobile number"
                />
              </div>

              <div className="firm-field">
                <label>Email</label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email address"
                />
              </div>

              <div className="firm-field full">
                <label>Address</label>
                <textarea
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Firm address"
                />
              </div>

              <div className="firm-field">
                <label>Bank Name</label>
                <input
                  name="bank_name"
                  value={form.bank_name}
                  onChange={handleChange}
                  placeholder="Bank name"
                />
              </div>

              <div className="firm-field">
                <label>Account No</label>
                <input
                  name="account_no"
                  value={form.account_no}
                  onChange={handleChange}
                  placeholder="Account number"
                />
              </div>

              <div className="firm-field">
                <label>IFSC Code</label>
                <input
                  name="ifsc_code"
                  value={form.ifsc_code}
                  onChange={handleChange}
                  placeholder="IFSC code"
                />
              </div>

              <div className="firm-field">
                <label>Logo</label>
                <input type="file" accept="image/*" onChange={handleLogo} />
              </div>

              <div className="firm-field full">
                <label>Terms & Conditions</label>
                <textarea
                  name="terms_conditions"
                  value={form.terms_conditions}
                  onChange={handleChange}
                  placeholder="Terms and conditions"
                />
              </div>

              {form.logo_url && (
                <div className="firm-logo-preview">
                  <img src={form.logo_url} alt="Firm Logo" />
                </div>
              )}
            </div>

            <div className="firm-actions">
              <button type="button" className="firm-cancel-btn" onClick={closeForm}>
                Cancel
              </button>

              <button type="submit" className="firm-save-btn">
                <Save size={18} />
                {editId ? "Update Firm" : "Save Firm"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="firm-index-page">
          <div className="firm-toolbar">
            <div className="firm-search-box">
              <Search size={20} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadFirms();
                }}
                placeholder="Search firm / mobile / GST"
              />
            </div>

            <button className="firm-add-btn" onClick={openAdd}>
              <Plus size={20} />
              NEW FIRM
            </button>
          </div>

          <div className="firm-table-card">
            <div className="firm-table-wrapper">
              <table className="firm-simple-table">
                <thead>
                  <tr>
                    <th>Logo</th>
                    <th>Firm Name</th>
                    <th>GST No</th>
                    <th>Mobile</th>
                    <th>Email</th>
                    <th>Bank</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="product-empty-row">
                        Loading firms...
                      </td>
                    </tr>
                  ) : firms.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="firm-empty-row">
                        No firms found
                      </td>
                    </tr>
                  ) : (
                    firms.map((firm) => (
                      <tr key={firm.id}>
                        <td>
                          {firm.logo_url ? (
                            <img className="firm-table-logo" src={firm.logo_url} alt="" />
                          ) : (
                            <span className="firm-no-logo">No Logo</span>
                          )}
                        </td>
                        <td>{firm.firm_name}</td>
                        <td>{firm.gst_no || "-"}</td>
                        <td>{firm.mobile || "-"}</td>
                        <td>{firm.email || "-"}</td>
                        <td>{firm.bank_name || "-"}</td>
                        <td>
                          <button
                            className="firm-action-btn edit"
                            onClick={() => editFirm(firm.id)}
                          >
                            <Edit size={22} />
                          </button>

                          <button
                            className="firm-action-btn delete"
                            onClick={() => deleteFirm(firm.id)}
                          >
                            <Trash2 size={22} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default Firm;